import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { TextField } from '@/components/ui/Field';
import {
  acceptNotice,
  eraseMyAccount,
  exportMyData,
  fetchConsent,
  withdrawConsent,
} from '@/api/endpoints/privacy';
import { errorMessage } from '@/api/errors';
import { queryKeys } from '@/api/queryKeys';
import { useAuth } from '@/auth/useAuth';

type Mode = 'export' | 'withdraw' | 'erase' | null;

function downloadJson(data: unknown) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'autoaware_my_data.json';
  link.click();
  URL.revokeObjectURL(url);
}

const TITLES = {
  export: 'Download my data',
  withdraw: 'Withdraw consent',
  erase: 'Erase my account',
} as const;

const DESCRIPTIONS = {
  export: 'A JSON file of everything we hold about you. Confirm your password.',
  withdraw:
    'This unlinks all your devices. Crash detection and crash alerts for them STOP until you accept the notice and link them again.',
  erase:
    'Permanently deletes your profile, contact details and location history and unlinks your devices, so crash alerts STOP. Crash records are kept without your name or numbers. This cannot be undone.',
} as const;

/** DPDP rights: consent status, download, withdraw, erase. The destructive ones say
 *  plainly what stops working, because "crash alerts stop" is the one consequence a
 *  person will not guess. */
export function PrivacyCard() {
  const queryClient = useQueryClient();
  const { signOut } = useAuth();
  const [mode, setMode] = useState<Mode>(null);
  const [password, setPassword] = useState('');

  const consent = useQuery({ queryKey: queryKeys.consent, queryFn: fetchConsent });

  function close() {
    setMode(null);
    setPassword('');
  }

  const accept = useMutation({
    mutationFn: acceptNotice,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.consent });
      toast.success('Thank you — your consent is recorded.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const action = useMutation({
    mutationFn: async (current: Exclude<Mode, null>) => {
      if (current === 'export') downloadJson(await exportMyData(password));
      else if (current === 'withdraw') await withdrawConsent();
      else await eraseMyAccount(password);
      return current;
    },
    onSuccess: async (done) => {
      close();
      if (done === 'erase') {
        toast.success('Your account has been erased.');
        signOut();
      } else if (done === 'withdraw') {
        await queryClient.invalidateQueries();
        toast.success('Consent withdrawn. Your devices are unlinked.');
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const upToDate = consent.data?.up_to_date ?? true;

  return (
    <>
      <Card>
        <CardHeader
          title="Privacy"
          description="Your data, your choices. See the full privacy notice for what we hold and why."
        />
        <CardBody>
          <div className="space-y-4 text-sm">
            {!upToDate && (
              <div className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-ink" role="status">
                You have not accepted the current{' '}
                <Link to="/privacy" target="_blank" className="font-medium text-accent">
                  privacy notice
                </Link>
                . You can keep using your linked devices, but linking a new one needs your consent.
                <div className="mt-2">
                  <Button variant="primary" loading={accept.isPending} onClick={() => accept.mutate()}>
                    Accept privacy notice
                  </Button>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setMode('export')}>Download my data</Button>
              <Button onClick={() => setMode('withdraw')}>Withdraw consent</Button>
              <Button variant="danger" onClick={() => setMode('erase')}>
                Erase my account
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Dialog
        open={mode !== null}
        onOpenChange={(open) => !open && close()}
        title={mode ? TITLES[mode] : ''}
        {...(mode ? { description: DESCRIPTIONS[mode] } : {})}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={close}>Cancel</Button>
            <Button
              variant={mode === 'export' ? 'primary' : 'danger'}
              loading={action.isPending}
              disabled={mode !== 'withdraw' && password.length === 0}
              onClick={() => mode && action.mutate(mode)}
            >
              {mode === 'export' ? 'Download' : mode === 'withdraw' ? 'Withdraw' : 'Erase permanently'}
            </Button>
          </div>
        }
      >
        {mode !== 'withdraw' && (
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        )}
      </Dialog>
    </>
  );
}
